import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Badge } from '../components/common/Badge';
import { Modal } from '../components/common/Modal';
import { Pagination, usePaginationState } from '../components/common/Pagination';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
/* G94 — re-skin chrome with v2. v1 hooks/handlers/api preserved. */
import {
  V2PageShell,
  Button as V2Button,
  Pill as V2Pill,
} from '../components/v2';
import {
  Plus, Zap, Edit3, Trash2, Link2, Unlink, RefreshCw, Download,
  BarChart3, Layers, X, CheckCircle, XCircle, ExternalLink, ChevronDown, ChevronRight,
  Shield, Database, Radio, Eye, ShoppingBag, Play, AlertTriangle, Settings, FileText,
  Cpu, Key, EyeOff, Plug,
} from 'lucide-react';
import { buildMarketplaceFromRegistry, findMarketplaceForInstalled } from '../data/marketplaceFromRegistry';
import {
  getConnectorConfig, saveConnectorConfig, authorizeConnector, getMyConnections,
  listConnectorManifests, listConnectorCategories,
} from '../services/api';

// ─── Connector type definitions ─────────────────────────────────────────────
const CONNECTOR_TYPES = [
  { value: 'LLM Provider', icon: Cpu, color: '#8b5cf6' },
  { value: 'SIEM / Log Management', icon: Eye, color: '#dc2626' },
  { value: 'EDR / XDR', icon: Radio, color: '#ea580c' },
  { value: 'Threat Intelligence', icon: Eye, color: '#7c3aed' },
  { value: 'Vulnerability Management', icon: AlertTriangle, color: '#b91c1c' },
  { value: 'Identity & Access Management', icon: Shield, color: '#2563eb' },
  { value: 'Identity Access Governance', icon: FileText, color: '#0d9488' },
  { value: 'Network Security', icon: ExternalLink, color: '#0891b2' },
  { value: 'Email Security', icon: Radio, color: '#c026d3' },
  { value: 'Cloud Security', icon: Layers, color: '#0284c7' },
  { value: 'Ticketing / Case Management', icon: Settings, color: '#16a34a' },
  { value: 'Communication', icon: Radio, color: '#d946ef' },
  { value: 'Fun / Utility', icon: Play, color: '#52525b' },
  { value: 'Identity Provider', icon: Shield, color: '#7c3aed' },
  { value: 'Cloud IAM', icon: Shield, color: '#2563eb' },
  { value: 'API Gateway', icon: ExternalLink, color: '#0891b2' },
  { value: 'Database', icon: Database, color: '#16a34a' },
  { value: 'Messaging', icon: Radio, color: '#d946ef' },
  { value: 'Storage', icon: Layers, color: '#52525b' },
];

const AUTH_METHODS = [
  { value: 'none', label: 'No Auth (Public API)' },
  { value: 'api_key', label: 'API Key' },
  { value: 'oauth2', label: 'OAuth 2.0' },
  { value: 'basic', label: 'Basic Auth' },
  { value: 'saml', label: 'SAML' },
  { value: 'mtls', label: 'Mutual TLS' },
];

const STATUS_COLORS = {
  connected: { bg: '#dcfce7', text: '#166534' },
  active: { bg: '#dbeafe', text: '#1e40af' },
  inactive: { bg: '#f4f4f5', text: '#52525b' },
  error: { bg: '#fecaca', text: '#991b1b' },
  testing: { bg: '#fef9c3', text: '#854d0e' },
};

const SEVERITY_COLORS = { critical: '#ef4444', high: '#d97706', medium: '#78716c', low: '#a8a29e' };
const EVAL_TYPE_COLORS = { functional: '#0d9488', negative: '#ef4444', guardrail: '#d97706', cost: '#8b5cf6', security: '#ec4899' };

// ─── Shared Styles ──────────────────────────────────────────────────────────
const cardStyle = { background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, padding: 16 };
const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #e4e4e7', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#52525b', marginBottom: 6 };
const metaLabelStyle = { fontSize: 10, color: '#a1a1aa', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 };
const btnBase = { padding: '7px 14px', borderRadius: 6, border: '1px solid #e4e4e7', background: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#52525b', display: 'flex', alignItems: 'center', gap: 5 };
const btnPrimary = { ...btnBase, border: 'none', background: '#52525b', color: '#fff' };

function ConnectorStatusBadge({ status }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.inactive;
  return (
    <span style={{ padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: colors.bg, color: colors.text, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {status === 'connected' && <CheckCircle size={11} />}
      {status}
    </span>
  );
}

function TierBadge({ tier }) {
  const colors = {
    free: { bg: '#dcfce7', text: '#166534' },
    premium: { bg: '#fef3c7', text: '#92400e' },
    enterprise: { bg: '#ede9fe', text: '#5b21b6' },
  };
  const c = colors[tier] || colors.free;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: c.bg, color: c.text }}>
      {tier}
    </span>
  );
}

function SeverityBadge({ severity }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
      background: `${SEVERITY_COLORS[severity]}18`, color: SEVERITY_COLORS[severity],
      border: `1px solid ${SEVERITY_COLORS[severity]}30`,
    }}>
      {severity}
    </span>
  );
}

function MethodBadge({ method }) {
  const colors = { GET: '#57534e', POST: '#0d9488', PUT: '#d97706', DELETE: '#ef4444' };
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      fontFamily: "'IBM Plex Mono', monospace",
      background: `${colors[method] || '#a8a29e'}15`, color: colors[method] || '#a8a29e',
    }}>
      {method}
    </span>
  );
}

// ─── Stat Card ──────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color = '#52525b' }) {
  return (
    <div style={{ ...cardStyle, padding: 20, flex: '1 1 180px', minWidth: 160 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 11, color: '#71717a', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#18181b' }}>{value}</div>
        </div>
        {Icon && <Icon size={20} style={{ color }} />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Marketplace Detail View ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
function MarketplaceDetailView({ connector, onClose, onInstall, installedIds }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSkill, setExpandedSkill] = useState(null);
  const isInstalled = installedIds.includes(connector.id);
  const tabs = ['overview', 'skills', 'guardrails', 'evals', 'config'];

  return (
    <div style={{ background: '#fafaf9', borderRadius: 10, border: '1px solid #e4e4e7', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e4e4e7', padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12, background: '#f4f4f5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
              border: '1px solid #e4e4e7',
            }}>
              {connector.icon}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, color: '#18181b', margin: 0 }}>{connector.name}</h2>
                <ConnectorStatusBadge status={connector.status} />
                <TierBadge tier={connector.tier} />
              </div>
              <div style={{ fontSize: 12, color: '#71717a', fontFamily: "'IBM Plex Mono', monospace" }}>
                v{connector.version} · {connector.skills.length} skills · {connector.guardrails.length} guardrails · {connector.evals.length} evals
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isInstalled ? (
              <button onClick={() => onInstall(connector)} style={{ ...btnPrimary, background: '#0d9488' }}>
                <Download size={13} /> Install Connector
              </button>
            ) : (
              <span style={{ padding: '7px 14px', borderRadius: 6, background: '#dcfce7', color: '#166534', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                <CheckCircle size={13} /> Installed
              </span>
            )}
            <button onClick={onClose} style={btnBase}><X size={13} /></button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e4e4e7', background: '#fff' }}>
        {tabs.map((t) => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            background: 'none', border: 'none',
            borderBottom: activeTab === t ? '2px solid #18181b' : '2px solid transparent',
            color: activeTab === t ? '#18181b' : '#a1a1aa',
            fontSize: 12, fontWeight: 600, padding: '10px 18px', cursor: 'pointer',
            textTransform: 'capitalize',
          }}>
            {t}
          </button>
        ))}
      </div>

      {/* Panel content */}
      <div style={{ padding: 24, minHeight: 300 }}>
        {/* ── Overview ── */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <p style={{ color: '#52525b', fontSize: 13, lineHeight: 1.7, maxWidth: 640, margin: 0 }}>
              {connector.description}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              {[
                ['Vendor', connector.vendor],
                ['Version', connector.version],
                ['Auth', connector.authType === 'none' ? 'No Auth' : connector.authType],
                ['Category', connector.category],
                ['Rate Limit', (() => {
                  const rl = connector.rateLimits || {};
                  const rpm = rl.rpm != null ? `${rl.rpm} RPM` : null;
                  const daily = rl.daily != null ? `${Number(rl.daily).toLocaleString()} daily` : null;
                  return [rpm, daily].filter(Boolean).join(' / ') || '—';
                })()],
                ['Tier', connector.tier],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={metaLabelStyle}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#18181b' }}>{value}</div>
                </div>
              ))}
            </div>
            <div>
              <div style={metaLabelStyle}>Tags</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {connector.tags.map((t) => (
                  <span key={t} style={{ padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 500, background: '#f4f4f5', color: '#52525b', border: '1px solid #e4e4e7' }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div style={metaLabelStyle}>Base URL</div>
              <code style={{ fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", color: '#0891b2', background: '#f0fdfa', padding: '6px 12px', borderRadius: 6, border: '1px solid #ccfbf1' }}>
                {connector.baseUrl}
              </code>
            </div>
            {connector.docs && (
              <a href={connector.docs} target="_blank" rel="noreferrer" style={{ color: '#0d9488', fontSize: 12, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                Vendor documentation ↗
              </a>
            )}
          </div>
        )}

        {/* ── Skills ── */}
        {activeTab === 'skills' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {connector.skills.map((skill) => {
              const isOpen = expandedSkill === skill.id;
              return (
                <div key={skill.id} style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
                  <button onClick={() => setExpandedSkill(isOpen ? null : skill.id)} style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', color: '#18181b',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <MethodBadge method={skill.method} />
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{skill.name}</span>
                      {skill.credentialType && (
                        <span style={{
                          padding: '2px 7px', borderRadius: 4, fontSize: 9, fontWeight: 600,
                          background: skill.credentialType === 'service_account' ? '#fef3c7' : skill.credentialType === 'user_oauth' ? '#dbeafe' : '#f4f4f5',
                          color: skill.credentialType === 'service_account' ? '#92400e' : skill.credentialType === 'user_oauth' ? '#1e40af' : '#52525b',
                          border: `1px solid ${skill.credentialType === 'service_account' ? '#fde68a' : skill.credentialType === 'user_oauth' ? '#bfdbfe' : '#e4e4e7'}`,
                        }}>
                          {skill.credentialType === 'user_oauth' ? 'User OAuth' : skill.credentialType === 'service_account' ? 'Service Account' : skill.credentialType}
                        </span>
                      )}
                    </div>
                    {isOpen ? <ChevronDown size={14} color="#a1a1aa" /> : <ChevronRight size={14} color="#a1a1aa" />}
                  </button>
                  {isOpen && (
                    <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid #f4f4f5' }}>
                      <p style={{ color: '#71717a', fontSize: 12, lineHeight: 1.6, margin: '12px 0 0' }}>{skill.description}</p>
                      {skill.accessDescription && (
                        <div style={{ padding: '6px 10px', borderRadius: 6, fontSize: 11, background: '#f5f5f4', border: '1px solid #a8a29e', color: '#57534e' }}>
                          {skill.accessDescription}
                        </div>
                      )}
                      <div>
                        <div style={metaLabelStyle}>Endpoint</div>
                        <code style={{ fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", color: '#0891b2' }}>
                          {connector.baseUrl}{skill.endpoint}
                        </code>
                      </div>
                      {skill.requiredScopes && (
                        <div>
                          <div style={metaLabelStyle}>Required Scopes</div>
                          <code style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", color: '#71717a', background: '#fafaf9', padding: '4px 8px', borderRadius: 4, border: '1px solid #f4f4f5', wordBreak: 'break-all' }}>
                            {skill.requiredScopes}
                          </code>
                        </div>
                      )}
                      {skill.params && skill.params.length > 0 && (
                      <div>
                        <div style={metaLabelStyle}>Parameters</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {skill.params.map((p) => (
                            <div key={p.name} style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                              background: '#fafaf9', borderRadius: 6, border: '1px solid #f4f4f5',
                              fontSize: 11, fontFamily: "'IBM Plex Mono', monospace",
                            }}>
                              <span style={{ color: '#18181b', minWidth: 130, fontWeight: 500 }}>{p.name}</span>
                              <span style={{ color: '#a1a1aa' }}>{p.type}</span>
                              {p.required && (
                                <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca' }}>
                                  required
                                </span>
                              )}
                              {p.example && <span style={{ color: '#a1a1aa', marginLeft: 'auto' }}>{p.example}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                      )}
                      {skill.guardrails && skill.guardrails.length > 0 && (
                        <div>
                          <div style={metaLabelStyle}>Skill Guardrails</div>
                          {skill.guardrails.map((g, i) => (
                            <div key={i} style={{ color: '#71717a', fontSize: 12, lineHeight: 1.6, paddingLeft: 12, borderLeft: '2px solid #e4e4e7', marginBottom: 4 }}>
                              {g}
                            </div>
                          ))}
                        </div>
                      )}
                      {skill.samplePayload && (
                        <div>
                          <div style={metaLabelStyle}>Sample Payload</div>
                          <pre style={{
                            background: '#fafaf9', border: '1px solid #e4e4e7', borderRadius: 8,
                            padding: 14, margin: 0, fontSize: 11, color: '#52525b',
                            fontFamily: "'IBM Plex Mono', monospace", overflowX: 'auto', lineHeight: 1.5,
                          }}>
                            {typeof skill.samplePayload === 'string' ? skill.samplePayload : JSON.stringify(skill.samplePayload, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Guardrails ── */}
        {activeTab === 'guardrails' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ color: '#71717a', fontSize: 12, margin: '0 0 8px', lineHeight: 1.6 }}>
              Enforced at the orchestrator layer before any API call is dispatched. Violations are logged to the tenant audit trail.
            </p>
            {connector.guardrails.map((g) => (
              <div key={g.id} style={{ ...cardStyle, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', marginTop: 5, flexShrink: 0,
                  background: SEVERITY_COLORS[g.severity], boxShadow: `0 0 6px ${SEVERITY_COLORS[g.severity]}44`,
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ color: '#a1a1aa', fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}>{g.id}</span>
                    <SeverityBadge severity={g.severity} />
                  </div>
                  <p style={{ color: '#52525b', fontSize: 13, margin: 0, lineHeight: 1.5 }}>{g.rule}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Evals ── */}
        {activeTab === 'evals' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ color: '#71717a', fontSize: 12, margin: '0 0 8px', lineHeight: 1.6 }}>
              Eval suite auto-generated per skill. Run on deploy and nightly via the Agentlets CI pipeline.
            </p>
            {connector.evals.map((e) => (
              <div key={e.name} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: '#f4f4f5',
                  border: '1px solid #e4e4e7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, flexShrink: 0,
                }}>
                  {e.type === 'functional' ? '✓' : e.type === 'negative' ? '✗' : e.type === 'guardrail' ? '⛨' : e.type === 'cost' ? '$' : '🔒'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ color: '#18181b', fontSize: 12, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{e.name}</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                      background: `${EVAL_TYPE_COLORS[e.type] || '#94a3b8'}15`,
                      color: EVAL_TYPE_COLORS[e.type] || '#94a3b8',
                    }}>
                      {e.type}
                    </span>
                  </div>
                  <p style={{ color: '#71717a', fontSize: 12, margin: 0 }}>{e.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Config ── */}
        {activeTab === 'config' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>
            <p style={{ color: '#71717a', fontSize: 12, margin: 0, lineHeight: 1.6 }}>
              Connector-level defaults. Skill invocations inherit these unless overridden.
            </p>

            {/* ── New configSchema-based rendering ── */}
            {connector.configSchema && connector.configSchema.length > 0 ? (
              <>
                {/* SMTP info banner for Email connectors */}
                {connector.authType === 'smtp_credentials' && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 8, background: '#f5f5f4', border: '1px solid #a8a29e',
                    fontSize: 12, color: '#1e40af', lineHeight: 1.5,
                  }}>
                    <strong>📧 SMTP Connector</strong> — Sends email directly via SMTP. Configure your provider credentials below.
                    No REST endpoint URL is needed.
                  </div>
                )}
                <div style={{ fontSize: 11, fontWeight: 600, color: '#18181b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: -8 }}>
                  Admin Configuration
                </div>
                {connector.configSchema.map((field) => (
                  <div key={field.field}>
                    <label style={labelStyle}>
                      {field.label}
                      {field.required && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
                      {field.encrypted && <Key size={10} style={{ marginLeft: 4, color: '#a1a1aa' }} />}
                    </label>
                    {field.type === 'select' && field.options ? (
                      <select
                        defaultValue={field.defaultValue || ''}
                        style={{ ...inputStyle, fontSize: 12, background: '#fff', cursor: 'pointer' }}
                      >
                        <option value="">— Select —</option>
                        {field.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.type === 'textarea' ? (
                      <textarea
                        placeholder={field.placeholder || ''}
                        defaultValue={field.defaultValue || ''}
                        rows={3}
                        style={{ ...inputStyle, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, resize: 'vertical', minHeight: 60 }}
                      />
                    ) : field.type === 'file' ? (
                      <div style={{ padding: '10px 14px', borderRadius: 6, border: '1px dashed #d4d4d8', background: '#fafaf9', fontSize: 11, color: '#71717a', textAlign: 'center' }}>
                        Drop {field.accept || ''} file here or click to browse
                      </div>
                    ) : (
                      <input
                        type={field.type === 'secret' ? 'password' : field.type || 'text'}
                        placeholder={field.placeholder || ''}
                        defaultValue={field.defaultValue || ''}
                        style={{ ...inputStyle, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}
                      />
                    )}
                    {field.helpText && (
                      <div style={{ fontSize: 10, color: '#a1a1aa', marginTop: 3, lineHeight: 1.4 }}>{field.helpText}</div>
                    )}
                  </div>
                ))}

                {/* Scope groups */}
                {connector.scopeGroups && connector.scopeGroups.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#18181b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                      OAuth Scope Groups
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {connector.scopeGroups.map((sg, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px',
                          background: '#fafaf9', borderRadius: 6, border: '1px solid #f4f4f5',
                        }}>
                          <span style={{
                            padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, flexShrink: 0, marginTop: 2,
                            background: sg.required ? '#dcfce7' : '#f4f4f5',
                            color: sg.required ? '#166534' : '#71717a',
                            border: `1px solid ${sg.required ? '#bbf7d0' : '#e4e4e7'}`,
                          }}>
                            {sg.required ? 'required' : 'optional'}
                          </span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#18181b' }}>{sg.group}</div>
                            <code style={{ fontSize: 10, color: '#71717a', wordBreak: 'break-all' }}>{sg.scopes}</code>
                            {sg.note && <div style={{ fontSize: 10, color: '#a1a1aa', marginTop: 2 }}>{sg.note}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* ── Legacy single-field auth rendering ── */}
                {connector.authType !== 'none' && (
                  <div>
                    <label style={labelStyle}>{connector.authType === 'api_key' ? 'API Key' : 'Credentials'}</label>
                    <input type="password" placeholder={connector.authField || 'Enter credentials'} style={{ ...inputStyle, fontFamily: "'IBM Plex Mono', monospace" }} />
                    <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 4 }}>Encrypted at rest via tenant vault. Never exposed to agent context.</div>
                  </div>
                )}
                {connector.authType === 'none' && (
                  <div style={{ padding: '10px 14px', borderRadius: 6, fontSize: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' }}>
                    <strong>No Auth</strong> — This connector calls the endpoint without credentials.
                  </div>
                )}
              </>
            )}

            {connector.configDefaults?.defaultFieldMask && (
              <div>
                <label style={labelStyle}>Default Field Mask</label>
                <input defaultValue={connector.configDefaults.defaultFieldMask} style={{ ...inputStyle, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }} />
                <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 4 }}>Controls billing tier. Basic fields = lowest cost.</div>
              </div>
            )}
            <div>
              <label style={labelStyle}>RPM Cap (Circuit Breaker)</label>
              <input type="number" defaultValue={connector.configDefaults?.rpmCap || connector.rateLimits.rpm} style={{ ...inputStyle, width: 120 }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'PII Auto-Purge (24h)', key: 'piiPurge' },
                { label: 'Audit Logging', key: 'auditLog' },
              ].filter(({ key }) => connector.configDefaults?.[key] !== undefined).map(({ label, key }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 36, height: 20, borderRadius: 10, background: connector.configDefaults[key] ? '#0d9488' : '#e4e4e7',
                    position: 'relative', cursor: 'pointer',
                  }}>
                    <div style={{
                      position: 'absolute', top: 2, left: connector.configDefaults[key] ? 18 : 2,
                      width: 16, height: 16, borderRadius: '50%', background: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)', transition: 'left 0.2s',
                    }} />
                  </div>
                  <span style={{ fontSize: 13, color: '#52525b' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// ─── Main Page ──────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
export default function ConnectorsPage() {
  const {
    connectors, loadConnectors, createConnector, updateConnector, deleteConnector,
    testConnector, testConnectorUrl, loadConnectorStats, loadConnectorSkills,
  } = usePlatform();
  const { session, hasPermission } = useAuth();
  const { addToast } = useToast();

  const orgId = session?.orgId;
  const tenantId = session?.tenantId;

  // RBAC permissions
  const canCreateConn = hasPermission('connector.create');
  const canEditConn = hasPermission('connector.edit');
  const canDeleteConn = hasPermission('connector.delete');
  const canTestConn = hasPermission('connector.test');

  const [viewMode, setViewMode] = useState('marketplace');
  const [showForm, setShowForm] = useState(false);
  const [editingConnector, setEditingConnector] = useState(null);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [testing, setTesting] = useState(null);
  const [stats, setStats] = useState(null);
  const [linkedSkills, setLinkedSkills] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [marketplaceDetail, setMarketplaceDetail] = useState(null);
  const [marketplaceCategoryFilter, setMarketplaceCategoryFilter] = useState('all');
  const pgConnectors = usePaginationState(10);

  const [form, setForm] = useState({
    name: '', description: '', type: 'API Gateway',
    url: '', authMethod: 'none', authHeader: '', apiKey: '',
  });
  const [configForm, setConfigForm] = useState({});  // configSchema field values
  const [marketplaceRef, setMarketplaceRef] = useState(null);  // matched marketplace connector
  const [showApiKey, setShowApiKey] = useState(false);
  const [urlTestResult, setUrlTestResult] = useState(null);
  const [testingUrl, setTestingUrl] = useState(false);

  // Live backend connector registry manifests (from ConnectorRegistryService).
  // These are the SINGLE SOURCE OF TRUTH for the marketplace — there is no
  // hand-maintained frontend copy anymore.
  const [registryManifests, setRegistryManifests] = useState([]);
  const [registryCategories, setRegistryCategories] = useState([]);
  const [registryLoaded, setRegistryLoaded] = useState(false);

  // Marketplace data derived from the registry. Equivalent in shape to the
  // legacy MARKETPLACE_CONNECTORS / MARKETPLACE_CATEGORIES constants, but
  // sourced from the backend so the catalogue stays in sync with the active
  // ConnectorManifest JSON files.
  const { connectors: marketplaceConnectors, categories: marketplaceCategories } =
    useMemo(
      () => buildMarketplaceFromRegistry(registryManifests, registryCategories),
      [registryManifests, registryCategories],
    );

  // Find which marketplace connector matches an installed connector.
  // Uses the shared fuzzy matcher so legacy names like "ServiceNow SecOps"
  // resolve to the current manifest "ServiceNow Security Operations" and the
  // OAuth2 grantType/password path renders correctly.
  const findMarketplaceRef = (conn) => findMarketplaceForInstalled(marketplaceConnectors, conn);

  // Track which marketplace connectors are installed (by matching via the
  // same fuzzy resolver used for detail rendering).
  const installedMarketplaceIds = marketplaceConnectors
    .filter((mc) => connectors.some((c) => findMarketplaceForInstalled([mc], c)))
    .map((mc) => mc.id);

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => { loadConnectors({ tenantId, orgId }); }, [loadConnectors, tenantId, orgId]);

  useEffect(() => {
    if (viewMode === 'stats') {
      loadConnectorStats().then(setStats);
    }
  }, [viewMode, loadConnectorStats]);

  // Fetch the backend ConnectorRegistryService manifests + categories once on
  // mount. This is the ONLY catalogue source — if the request fails we render
  // an empty marketplace with an error hint rather than falling back to stale
  // hardcoded data.
  useEffect(() => {
    // apiFetch returns {ok, data} — unwrap .data and fall back to the raw
    // value so we're resilient to either shape. Without this, Array.isArray
    // fails on the wrapper object and the marketplace renders empty.
    Promise.all([
      listConnectorManifests().catch(() => ({ ok: false, data: [] })),
      listConnectorCategories().catch(() => ({ ok: false, data: [] })),
    ]).then(([manifestsRes, catsRes]) => {
      const manifests = Array.isArray(manifestsRes?.data) ? manifestsRes.data
                       : Array.isArray(manifestsRes) ? manifestsRes
                       : [];
      const cats = Array.isArray(catsRes?.data) ? catsRes.data
                  : Array.isArray(catsRes) ? catsRes
                  : [];
      setRegistryManifests(manifests);
      setRegistryCategories(cats);
      setRegistryLoaded(true);
    });
  }, []);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await loadConnectors({ tenantId, orgId });
      if (viewMode === 'stats') {
        const s = await loadConnectorStats();
        setStats(s);
      }
    } catch (err) {
      console.error('Refresh failed:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // ── Create ────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!hasPermission('connector.create')) {
      addToast('You do not have permission to create connectors', 'error');
      return;
    }
    if (!form.name.trim() || !form.url.trim()) {
      addToast('Name and URL required', 'warn');
      return;
    }
    try {
      const payload = {
        name: form.name, description: form.description, type: form.type,
        url: form.url, authMethod: form.authMethod,
        authHeader: form.authHeader || undefined,
        apiKey: form.apiKey || undefined,
        tenantId, orgId,
      };
      const result = await createConnector(payload);
      if (result?.ok) {
        addToast(`Connector "${form.name}" created`, 'success');
        setShowForm(false);
        resetForm();
      } else {
        addToast(`Failed: ${result?.error || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      console.error('Create connector failed:', err);
      addToast('Network error creating connector', 'error');
    }
  };

  // ── Install from marketplace ──────────────────────────────────────────────
  const handleInstallMarketplace = async (mc) => {
    try {
      const payload = {
        name: mc.name,
        description: mc.description,
        type: mc.category,
        url: mc.baseUrl || '',  // SMTP and other non-REST connectors may have null/placeholder baseUrl
        authMethod: mc.authType || 'none',
        authHeader: mc.authField || '',
        tenantId, orgId,
      };
      const result = await createConnector(payload);
      if (result?.ok) {
        const hasConfig = mc.configSchema && mc.configSchema.length > 0;
        addToast(
          hasConfig
            ? `"${mc.name}" installed — open Edit to configure credentials and connection settings.`
            : `"${mc.name}" installed — open Edit to add your API key, then Test the connection.`,
          'success'
        );
        setMarketplaceDetail(null);
      } else {
        addToast(`Failed to install: ${result?.error || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      console.error('Install marketplace connector failed:', err);
      addToast('Network error installing connector', 'error');
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const openEdit = (conn) => {
    setEditingConnector(conn);
    setShowApiKey(false);
    setForm({
      name: conn.name || '', description: conn.description || '',
      type: conn.type || 'API Gateway', url: conn.url || '',
      authMethod: conn.authMethod || 'none',
      authHeader: conn.authHeader || '',
      apiKey: '',  // write-only field — always blank on edit; user re-enters to update
    });
    // Check if this is a marketplace connector with configSchema
    const mcRef = findMarketplaceRef(conn);
    setMarketplaceRef(mcRef);
    if (mcRef?.configSchema) {
      // Initialize configForm with defaults from schema
      const defaults = {};
      mcRef.configSchema.forEach(f => {
        defaults[f.field] = f.defaultValue || '';
      });
      setConfigForm(defaults);
      // Try to load existing config from backend
      getConnectorConfig(conn.id).then(result => {
        const data = result?.data;
        if (data && data.configStatus !== 'unconfigured') {
          const loaded = { ...defaults };
          const pc = data.providerConfig || {};
          const oauthFields = new Set([
            'oauthClientId', 'oauthClientSecret', 'oauthRedirectUri',
            'oauthAuthUrl', 'oauthTokenUrl', 'oauthUserinfoUrl', 'oauthScopes',
            'serviceAccountJson', 'delegatedAdmin', 'baseUrlOverride',
          ]);

          // Secret fields that are stored in encrypted columns — read back from those columns
          const secretFieldReadMap = {
            'smtp_password': 'oauthClientSecret',
            'smtp_username': 'oauthClientId',
            'client_secret': 'oauthClientSecret',
            'client_id': 'oauthClientId',
            'api_token': 'oauthClientSecret',
          };

          mcRef.configSchema.forEach(f => {
            const parts = f.field.split('.');
            if (parts.length > 1 && parts[0] === 'providerConfig') {
              // Explicit providerConfig.xxx notation → read from providerConfig sub-object
              const subKey = parts.slice(1).join('.');
              if (pc[subKey] !== undefined && pc[subKey] !== null) {
                // For secret fields in providerConfig (e.g., password), show sentinel instead of real value
                if (f.type === 'secret') {
                  loaded[f.field] = '••••••••'; // sentinel = "password saved, don't overwrite"
                } else {
                  loaded[f.field] = String(pc[subKey]);
                }
              }
            } else if (secretFieldReadMap[f.field]) {
              // Secret/credential field → read from its encrypted DB column
              const dbCol = secretFieldReadMap[f.field];
              if (data[dbCol] !== undefined && data[dbCol] !== null) {
                loaded[f.field] = String(data[dbCol]);
              }
              // Note: oauthClientSecret has @JsonProperty(WRITE_ONLY) so it won't appear in GET responses.
              // For password fields, use sentinel placeholder so user knows a value IS saved.
              // The sentinel '••••••••' is skipped on save (meaning "keep existing").
              // If user clears it and types a new value, that new value gets saved.
              if (f.type === 'secret' && !loaded[f.field]) {
                if (data.hasClientSecret || data.configStatus === 'configured') {
                  loaded[f.field] = '••••••••'; // sentinel = "password already saved, don't overwrite"
                }
              }
            } else if (oauthFields.has(f.field)) {
              // Known OAuth field → read from top-level
              if (data[f.field] !== undefined && data[f.field] !== null) {
                loaded[f.field] = String(data[f.field]);
              }
              // Apply sentinel for WRITE_ONLY secret fields (e.g., oauthClientSecret)
              if (f.type === 'secret' && !loaded[f.field]) {
                if (data.hasClientSecret || data.configStatus === 'configured') {
                  loaded[f.field] = '••••••••';
                }
              }
            } else {
              // All other configSchema fields → read from providerConfig JSONB
              if (pc[f.field] !== undefined && pc[f.field] !== null) {
                loaded[f.field] = String(pc[f.field]);
              } else if (data[f.field] !== undefined && data[f.field] !== null) {
                // Fallback: check top-level in case of legacy data
                loaded[f.field] = String(data[f.field]);
              }
            }
          });
          // Apply provider presets for fields that are still empty after load
          // e.g., if smtp_provider='gmail' is saved but smtp_host wasn't persisted
          if (mcRef.providerPresets && loaded.smtp_provider && mcRef.providerPresets[loaded.smtp_provider]) {
            const preset = mcRef.providerPresets[loaded.smtp_provider];
            Object.entries(preset).forEach(([k, v]) => {
              if (!loaded[k] || loaded[k] === '') {
                loaded[k] = String(v);
              }
            });
          }
          console.log('[Config Load] loaded:', JSON.stringify(loaded));
          setConfigForm(loaded);
        }
      }).catch(() => {/* ignore — config may not exist yet */});
    } else {
      setMarketplaceRef(null);
      setConfigForm({});
    }
  };

  const handleUpdate = async () => {
    if (!hasPermission('connector.edit')) {
      addToast('You do not have permission to edit connectors', 'error');
      return;
    }
    if (!editingConnector) return;
    try {
      // 1. Update base connector record
      const payload = {
        name: form.name, description: form.description, type: form.type,
        url: form.url, authMethod: form.authMethod,
        authHeader: form.authHeader || undefined,
        apiKey: form.apiKey || undefined,
        tenantId, orgId,
      };
      const result = await updateConnector(editingConnector.id, payload);

      // 2. Save configSchema fields independently (don't gate on connector update)
      let configSaved = false;
      console.log('[Config Save] marketplaceRef:', marketplaceRef?.name, 'hasConfigSchema:', !!marketplaceRef?.configSchema, 'configForm keys:', Object.keys(configForm), 'configForm:', JSON.stringify(configForm));
      if (marketplaceRef?.configSchema && Object.keys(configForm).length > 0) {
        // ── Validate required configSchema fields before saving ──────────
        const missingFields = marketplaceRef.configSchema
          .filter(f => {
            if (!f.required) return false;
            const val = configForm[f.field];
            // sentinel '••••••••' means value is already saved — that's fine
            if (val === '••••••••') return false;
            return !val || (typeof val === 'string' && val.trim() === '');
          })
          .map(f => f.label);

        if (missingFields.length > 0) {
          addToast(`Missing required fields: ${missingFields.join(', ')}`, 'error');
          return;
        }

        try {
          // Build the backend payload.
          // Fields prefixed with "providerConfig." go into providerConfig as nested objects.
          // Named OAuth fields (oauthClientId, etc.) go as top-level keys.
          // ALL other flat keys from configSchema go into providerConfig so they're stored in JSONB.
          const backendPayload = {};
          const providerConfig = {};
          const oauthFields = new Set([
            'oauthClientId', 'oauthClientSecret', 'oauthRedirectUri',
            'oauthAuthUrl', 'oauthTokenUrl', 'oauthUserinfoUrl', 'oauthScopes',
            'serviceAccountJson', 'delegatedAdmin', 'baseUrlOverride',
          ]);

          // Fields that contain secrets → route to encrypted DB columns instead of plain-text JSONB
          const secretFieldMap = {
            'smtp_password': 'oauthClientSecret',   // encrypted at rest via ApiKeyEncryptionConverter
            'smtp_username': 'oauthClientId',        // stored in dedicated column (not secret, but paired with password)
            'client_secret': 'oauthClientSecret',    // EDR connector client secrets → encrypted column
            'client_id': 'oauthClientId',            // EDR connector client IDs → dedicated column
            'api_token': 'oauthClientSecret',        // SentinelOne API token → encrypted column
          };

          for (const [key, value] of Object.entries(configForm)) {
            if (value === '' || value === undefined) continue;  // skip empty fields
            if (value === '••••••••') continue;  // sentinel = "password unchanged, don't overwrite"
            const parts = key.split('.');
            if (parts.length > 1 && parts[0] === 'providerConfig') {
              // Explicit providerConfig.xxx notation
              providerConfig[parts.slice(1).join('.')] = value;
            } else if (secretFieldMap[key]) {
              // Secret/credential field → route to encrypted DB column
              backendPayload[secretFieldMap[key]] = value;
            } else if (oauthFields.has(key)) {
              // Known OAuth / top-level field — set directly
              backendPayload[key] = value;
            } else {
              // All other configSchema fields → store in providerConfig JSONB
              providerConfig[key] = value;
            }
          }

          if (Object.keys(providerConfig).length > 0) {
            backendPayload.providerConfig = providerConfig;
          }

          console.log('[Config Save] payload:', JSON.stringify(backendPayload));
          const cfgResult = await saveConnectorConfig(editingConnector.id, backendPayload);
          configSaved = cfgResult?.ok;
          if (!configSaved) {
            console.warn('Config save response:', cfgResult);
          }
        } catch (cfgErr) {
          console.warn('Config save failed:', cfgErr);
          addToast('Connector updated but configuration save failed: ' + cfgErr.message, 'warning');
        }
      }

      if (result?.ok || configSaved) {
        addToast(
          configSaved
            ? `Connector "${form.name}" configuration saved`
            : `Connector "${form.name}" updated`,
          'success'
        );
        setEditingConnector(null);
        setMarketplaceRef(null);
        setConfigForm({});
        resetForm();
      } else {
        addToast(`Failed: ${result?.error || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      console.error('Update connector failed:', err);
      addToast('Network error updating connector', 'error');
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (connId, name) => {
    if (!window.confirm(`Delete connector "${name}"? This will unlink all connected skills.`)) return;
    try {
      const result = await deleteConnector(connId);
      if (result?.ok) {
        addToast(`Connector "${name}" deleted`, 'success');
        if (selectedConnector?.id === connId) setSelectedConnector(null);
      } else {
        addToast(`Failed: ${result?.error || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      console.error('Delete connector failed:', err);
      addToast('Network error deleting connector', 'error');
    }
  };

  // ── OAuth: Connect My Account ─────────────────────────────────────────────
  const [connectingOAuth, setConnectingOAuth] = useState(null);
  const [myConnections, setMyConnections] = useState({});

  // Load user's connection status for all connectors
  const loadMyConnections = useCallback(async () => {
    try {
      const result = await getMyConnections();
      if (result?.ok && result.data) {
        // Transform array into { connectorId: connectionInfo } map
        const map = {};
        (Array.isArray(result.data) ? result.data : []).forEach(c => {
          if (c.connectorId) map[c.connectorId] = c;
        });
        setMyConnections(map);
      }
    } catch (err) {
      console.error('Failed to load connections:', err);
    }
  }, []);

  useEffect(() => { loadMyConnections(); }, [loadMyConnections]);

  // Check if returning from OAuth callback (via react-router state)
  useEffect(() => {
    // The ConnectorOAuthCallbackPage passes state.connectorConnected when redirecting back
    if (window.history.state?.usr?.connectorConnected) {
      loadMyConnections();
      addToast('Account connected! OAuth tokens saved.', 'success');
    }
  }, []);

  const handleConnectAccount = async (connId) => {
    try {
      setConnectingOAuth(connId);
      const result = await authorizeConnector(connId);
      if (result?.ok && result.data?.authorizationUrl) {
        // Redirect to provider's OAuth consent page
        window.location.href = result.data.authorizationUrl;
      } else {
        const errMsg = result?.data?.error || result?.error || 'Failed to initiate OAuth flow';
        addToast(errMsg, 'error');
        setConnectingOAuth(null);
      }
    } catch (err) {
      console.error('Connect account failed:', err);
      addToast('Failed to start OAuth: ' + err.message, 'error');
      setConnectingOAuth(null);
    }
  };

  // ── Test ──────────────────────────────────────────────────────────────────
  const handleTest = async (connId) => {
    try {
      setTesting(connId);
      const result = await testConnector(connId);
      if (result?.ok) {
        const statusLabel = result.data?.status === 'connected' ? 'connected' : 'passed';
        addToast(`Connection test ${statusLabel} ✓`, 'success');
        // If we're viewing this connector in the detail modal, refresh it
        if (selectedConnector?.id === connId && result.data) {
          setSelectedConnector(result.data);
        }
      } else {
        addToast(`Test failed: ${result?.error || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      console.error('Test connector failed:', err);
      addToast('Network error testing connector', 'error');
    } finally {
      setTesting(null);
    }
  };

  // ── Detail view with linked skills ────────────────────────────────────────
  const openDetail = async (conn) => {
    try {
      setSelectedConnector(conn);
      const [skills, cfgResult] = await Promise.all([
        loadConnectorSkills(conn.id),
        getConnectorConfig(conn.id),
      ]);
      setLinkedSkills(skills);
      // Enrich selectedConnector with config status for the detail modal
      if (cfgResult?.data) {
        const cfg = cfgResult.data;
        const isConfigured = cfg.configStatus === 'configured';
        const hasOAuth = !!(cfg.oauthClientId && cfg.oauthClientId.trim());
        const hasSecret = !!cfg.hasClientSecret;
        const hasProviderData = !!(cfg.providerConfig && Object.keys(cfg.providerConfig).length > 0);
        setSelectedConnector(prev => ({
          ...prev,
          _configStatus: cfg.configStatus,
          _hasOAuthCreds: hasOAuth || hasSecret,
          _hasProviderConfig: hasProviderData,
          _isConfigured: isConfigured || hasOAuth || hasSecret || hasProviderData,
        }));
      }
    } catch (err) {
      console.error('Failed to load connector details:', err);
      setLinkedSkills([]);
    }
  };

  const resetForm = () => {
    setForm({ name: '', description: '', type: 'API Gateway', url: '', authMethod: 'none', authHeader: '', apiKey: '' });
    setConfigForm({});
    setMarketplaceRef(null);
    setUrlTestResult(null);
    setTestingUrl(false);
    setShowApiKey(false);
  };

  const handleTestUrl = async () => {
    if (!form.url.trim()) {
      addToast('Enter a URL first', 'warn');
      return;
    }
    setTestingUrl(true);
    setUrlTestResult(null);
    try {
      const result = await testConnectorUrl(form.url.trim());
      if (result?.ok) {
        setUrlTestResult(result.data);
      } else {
        setUrlTestResult({ reachable: false, error: result?.error || 'Request failed' });
      }
    } catch (err) {
      setUrlTestResult({ reachable: false, error: err.message });
    } finally {
      setTestingUrl(false);
    }
  };

  const getTypeInfo = (type) => CONNECTOR_TYPES.find((t) => t.value === type) || CONNECTOR_TYPES[0];

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <V2PageShell
      title="Connectors"
      subtitle="Identity providers, connectors, and data sources linked to agent skills."
      tags={<V2Pill tone="cyan" size="sm">Build</V2Pill>}
      actions={
        <>
          <V2Button
            variant="ghost"
            size="sm"
            iconLeft={<RefreshCw size={13} className={refreshing ? 'spin' : ''} />}
            onClick={handleRefresh}
          >
            Refresh
          </V2Button>
          {canCreateConn && (
            <V2Button
              variant="primary"
              size="sm"
              iconLeft={<Plus size={14} />}
              onClick={() => { resetForm(); setShowForm(true); }}
            >
              Add Connector
            </V2Button>
          )}
        </>
      }
    >

      {/* View Toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[
          { id: 'marketplace', label: 'Marketplace', icon: ShoppingBag },
          { id: 'grid', label: 'Installed', icon: Layers },
          { id: 'stats', label: 'Dashboard', icon: BarChart3 },
        ].map((m) => (
          <button key={m.id} onClick={() => { setViewMode(m.id); setMarketplaceDetail(null); }} style={{
            padding: '8px 16px', borderRadius: 6,
            border: viewMode === m.id ? 'none' : '1px solid #e4e4e7',
            background: viewMode === m.id ? '#52525b' : '#fff',
            color: viewMode === m.id ? '#fff' : '#52525b',
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <m.icon size={14} /> {m.label}
          </button>
        ))}
      </div>

      {/* ═══ Grid View ═══════════════════════════════════════════════════════ */}
      {viewMode === 'grid' && (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {connectors.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#71717a', padding: 60, fontSize: 13 }}>
              No connectors found. Add one or browse the <button onClick={() => setViewMode('marketplace')} style={{ background: 'none', border: 'none', color: '#0d9488', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Marketplace</button>.
            </div>
          ) : pgConnectors.paginate(connectors).map((conn) => {
            const typeInfo = getTypeInfo(conn.type);
            const TypeIcon = typeInfo.icon;
            return (
              <div key={conn.id} style={{
                background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, padding: 16,
                cursor: 'pointer', transition: 'box-shadow 0.15s',
              }} onClick={() => openDetail(conn)}>
                <div style={{ height: 3, borderRadius: 2, background: typeInfo.color, marginBottom: 14 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8, background: `${typeInfo.color}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <TypeIcon size={18} style={{ color: typeInfo.color }} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: 14, fontWeight: 600, color: '#18181b', margin: 0 }}>{conn.name}</h3>
                      <p style={{ fontSize: 11, color: '#71717a', margin: '2px 0 0 0' }}>{conn.type}</p>
                    </div>
                  </div>
                  <ConnectorStatusBadge status={conn.status} />
                </div>
                {conn.description && (
                  <p style={{ fontSize: 12, color: '#71717a', margin: '0 0 12px 0', lineHeight: 1.4 }}>
                    {conn.description.length > 80 ? conn.description.slice(0, 80) + '...' : conn.description}
                  </p>
                )}
                <div style={{ fontSize: 11, color: '#a1a1aa', marginBottom: 12, display: 'flex', gap: 12 }}>
                  {conn.authMethod && <span>Auth: {conn.authMethod === 'none' ? 'No Auth' : conn.authMethod}</span>}
                  {conn.lastTestResult && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      {conn.lastTestResult === 'SUCCESS' ? <CheckCircle size={11} style={{ color: '#16a34a' }} /> : <XCircle size={11} style={{ color: '#ef4444' }} />}
                      {conn.lastTestResult}
                    </span>
                  )}
                  {conn.lastSyncedAt && <span>Synced: {new Date(conn.lastSyncedAt).toLocaleDateString()}</span>}
                </div>
                <div style={{ display: 'flex', gap: 6, borderTop: '1px solid #f4f4f5', paddingTop: 12 }}>
                  {canTestConn && (
                    <button onClick={(e) => { e.stopPropagation(); handleTest(conn.id); }}
                      disabled={testing === conn.id} style={{
                      flex: 1, padding: '7px 0', borderRadius: 6, border: '1px solid #e4e4e7',
                      background: '#fff', cursor: testing === conn.id ? 'not-allowed' : 'pointer',
                      fontSize: 11, fontWeight: 600, color: '#52525b', opacity: testing === conn.id ? 0.5 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}>
                      <Zap size={12} /> {testing === conn.id ? 'Testing...' : 'Test'}
                    </button>
                  )}
                  {canEditConn && (
                    <button onClick={(e) => { e.stopPropagation(); openEdit(conn); }} style={{
                      padding: '7px 12px', borderRadius: 6, border: '1px solid #e4e4e7',
                      background: '#fff', cursor: 'pointer', color: '#71717a',
                    }}>
                      <Edit3 size={13} />
                    </button>
                  )}
                  {canDeleteConn && (
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(conn.id, conn.name); }} style={{
                      padding: '7px 12px', borderRadius: 6, border: '1px solid #fecaca',
                      background: '#fff', cursor: 'pointer', color: '#ef4444',
                    }}>
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <Pagination totalItems={connectors.length} page={pgConnectors.page} pageSize={pgConnectors.pageSize} onPageChange={pgConnectors.setPage} onPageSizeChange={pgConnectors.setPageSize} noun="connectors" />
      </>)}

      {/* ═══ Marketplace View ═════════════════════════════════════════════════ */}
      {viewMode === 'marketplace' && !marketplaceDetail && (() => {
        const filteredCategories = marketplaceCategoryFilter === 'all'
          ? marketplaceCategories
          : marketplaceCategories.filter(c => c.id === marketplaceCategoryFilter);
        const connectorsByCategory = {};
        marketplaceConnectors.forEach(mc => {
          const cat = mc.marketplaceCategory || 'uncategorized';
          if (!connectorsByCategory[cat]) connectorsByCategory[cat] = [];
          connectorsByCategory[cat].push(mc);
        });
        return (
          <div>
            <p style={{ color: '#71717a', fontSize: 13, marginBottom: 16 }}>
              Pre-built connectors with skills, guardrails, and eval suites ready to install.
            </p>

            {/* ═══ Registry Load Status ═══════════════════════════════════════════
                The entire marketplace grid below is sourced directly from the
                backend ConnectorRegistryService. This compact banner confirms
                the load state for engineers and ops. */}
            {registryLoaded && (
              <div style={{
                marginBottom: 20, padding: '8px 14px', borderRadius: 8,
                border: '1px solid #e0e7ff', background: '#faf7ff',
                display: 'flex', alignItems: 'center', gap: 10, fontSize: 12,
              }}>
                <Zap size={14} color="#8b5cf6" />
                <span style={{ color: '#52525b' }}>
                  Served from{' '}
                  <code style={{ fontSize: 11, background: '#f4f4f5', padding: '1px 4px', borderRadius: 3 }}>
                    ConnectorRegistryService
                  </code>
                  {' · '}<strong>{registryManifests.length}</strong> manifest{registryManifests.length !== 1 ? 's' : ''}
                  {' · '}<strong>{marketplaceCategories.length}</strong> categor{marketplaceCategories.length !== 1 ? 'ies' : 'y'}
                </span>
                <span style={{
                  marginLeft: 'auto', padding: '2px 8px', borderRadius: 99,
                  fontSize: 9, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                  background: registryManifests.length > 0 ? '#dcfce7' : '#fee2e2',
                  color: registryManifests.length > 0 ? '#166534' : '#991b1b',
                }}>
                  {registryManifests.length > 0 ? '● live' : '● empty'}
                </span>
              </div>
            )}

            {/* Category filter pills */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
              <button
                onClick={() => setMarketplaceCategoryFilter('all')}
                style={{
                  ...btnBase,
                  background: marketplaceCategoryFilter === 'all' ? '#18181b' : '#fff',
                  color: marketplaceCategoryFilter === 'all' ? '#fff' : '#52525b',
                  borderColor: marketplaceCategoryFilter === 'all' ? '#18181b' : '#e4e4e7',
                  fontSize: 11, padding: '5px 12px', borderRadius: 99,
                }}
              >
                All ({marketplaceConnectors.length})
              </button>
              {marketplaceCategories.map(cat => {
                const count = (connectorsByCategory[cat.id] || []).length;
                if (count === 0) return null;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setMarketplaceCategoryFilter(cat.id)}
                    style={{
                      ...btnBase,
                      background: marketplaceCategoryFilter === cat.id ? cat.color : '#fff',
                      color: marketplaceCategoryFilter === cat.id ? '#fff' : '#52525b',
                      borderColor: marketplaceCategoryFilter === cat.id ? cat.color : '#e4e4e7',
                      fontSize: 11, padding: '5px 12px', borderRadius: 99,
                    }}
                  >
                    {cat.icon} {cat.name} ({count})
                  </button>
                );
              })}
            </div>

            {/* Category groups */}
            {filteredCategories.map(cat => {
              const catConnectors = connectorsByCategory[cat.id] || [];
              if (catConnectors.length === 0) return null;
              return (
                <div key={cat.id} style={{ marginBottom: 28 }}>
                  {/* Category header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <span style={{
                      width: 32, height: 32, borderRadius: 8, background: `${cat.color}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                      border: `1px solid ${cat.color}30`,
                    }}>
                      {cat.icon}
                    </span>
                    <div>
                      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#18181b', margin: 0 }}>{cat.name}</h3>
                      <span style={{ fontSize: 11, color: '#a1a1aa' }}>
                        {cat.description} · {catConnectors.length} connector{catConnectors.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Connector cards grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
                    {catConnectors.map((mc) => {
                      const isInstalled = installedMarketplaceIds.includes(mc.id);
                      return (
                        <div key={mc.id} onClick={() => setMarketplaceDetail(mc)} style={{
                          ...cardStyle, cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s',
                          borderColor: isInstalled ? '#bbf7d0' : '#e4e4e7',
                          borderLeft: `3px solid ${cat.color}`,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{
                                width: 36, height: 36, borderRadius: 8, background: '#f4f4f5',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                                border: '1px solid #e4e4e7',
                              }}>
                                {mc.icon}
                              </div>
                              <div>
                                <h4 style={{ fontSize: 13, fontWeight: 600, color: '#18181b', margin: 0 }}>{mc.name}</h4>
                                <div style={{ fontSize: 10, color: '#71717a', marginTop: 1 }}>
                                  {mc.vendor} · v{mc.version}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <TierBadge tier={mc.tier} />
                              {isInstalled && (
                                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: '#dcfce7', color: '#166534' }}>
                                  installed
                                </span>
                              )}
                            </div>
                          </div>
                          <p style={{ fontSize: 12, color: '#71717a', margin: '0 0 10px 0', lineHeight: 1.4 }}>
                            {mc.description.length > 120 ? mc.description.slice(0, 120) + '...' : mc.description}
                          </p>
                          <div style={{ display: 'flex', gap: 10, fontSize: 10, color: '#a1a1aa', marginBottom: 8 }}>
                            <span>{mc.skills?.length || 0} skills</span>
                            <span>{mc.guardrails?.length || 0} guardrails</span>
                            <span>{mc.evals?.length || 0} evals</span>
                            <span>Auth: {mc.authType === 'none' ? 'None' : mc.authType}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {mc.tags.slice(0, 4).map((t) => (
                              <span key={t} style={{ padding: '2px 6px', borderRadius: 99, fontSize: 9, background: '#f4f4f5', color: '#71717a', border: '1px solid #e4e4e7' }}>
                                {t}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ═══ Marketplace Detail ════════════════════════════════════════════════ */}
      {viewMode === 'marketplace' && marketplaceDetail && (
        <MarketplaceDetailView
          connector={marketplaceDetail}
          onClose={() => setMarketplaceDetail(null)}
          onInstall={handleInstallMarketplace}
          installedIds={installedMarketplaceIds}
        />
      )}

      {/* ═══ Stats Dashboard ═════════════════════════════════════════════════ */}
      {viewMode === 'stats' && (
        <div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <StatCard label="Total Connectors" value={stats?.totalConnectors ?? 0} icon={Layers} />
            <StatCard label="Active" value={stats?.activeCount ?? 0} icon={CheckCircle} color="#16a34a" />
            <StatCard label="Healthy %" value={`${stats?.healthyPercentage ?? 0}%`} icon={Zap} color="#16a34a" />
            <StatCard label="Skill Links" value={stats?.totalSkillLinks ?? 0} icon={Link2} color="#7c3aed" />
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1, background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, padding: 20 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: '#18181b', margin: '0 0 16px 0' }}>By Type</h4>
              {stats?.byType && Object.entries(stats.byType).map(([type, count]) => (
                <div key={type} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f4f4f5' }}>
                  <span style={{ fontSize: 13, color: '#52525b' }}>{type}</span>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#18181b' }}>{count}</span>
                </div>
              ))}
              {(!stats?.byType || Object.keys(stats.byType).length === 0) && (
                <div style={{ color: '#a1a1aa', fontSize: 12 }}>No data</div>
              )}
            </div>
            <div style={{ flex: 1, background: '#fff', border: '1px solid #e4e4e7', borderRadius: 8, padding: 20 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: '#18181b', margin: '0 0 16px 0' }}>By Status</h4>
              {stats?.byStatus && Object.entries(stats.byStatus).map(([status, count]) => (
                <div key={status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f4f4f5' }}>
                  <ConnectorStatusBadge status={status} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#18181b' }}>{count}</span>
                </div>
              ))}
              {(!stats?.byStatus || Object.keys(stats.byStatus).length === 0) && (
                <div style={{ color: '#a1a1aa', fontSize: 12 }}>No data</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ Detail Modal ═══════════════════════════════════════════════════ */}
      {selectedConnector && (
        <Modal isOpen={!!selectedConnector} title={selectedConnector.name}
          onClose={() => { setSelectedConnector(null); setLinkedSkills([]); }}
          actions={
            <div style={{ display: 'flex', gap: 8 }}>
              {canTestConn && (
                <button onClick={() => handleTest(selectedConnector.id)}
                  disabled={testing === selectedConnector.id} style={{
                  padding: '8px 16px', borderRadius: 6, border: '1px solid #e4e4e7',
                  background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#52525b',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <Zap size={12} /> {testing === selectedConnector.id ? 'Testing...' : 'Test Connection'}
                </button>
              )}
              <button onClick={() => { setSelectedConnector(null); setLinkedSkills([]); }} style={{
                padding: '8px 16px', borderRadius: 6, border: '1px solid #e4e4e7',
                background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              }}>Close</button>
            </div>
          }>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: '#a1a1aa', marginBottom: 4 }}>TYPE</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#18181b' }}>{selectedConnector.type}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#a1a1aa', marginBottom: 4 }}>STATUS</div>
                <ConnectorStatusBadge status={selectedConnector.status} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#a1a1aa', marginBottom: 4 }}>AUTH METHOD</div>
                <div style={{ fontSize: 13, color: '#52525b' }}>{selectedConnector.authMethod === 'none' ? 'No Auth (Public)' : (selectedConnector.authMethod || '—')}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#a1a1aa', marginBottom: 4 }}>CREDENTIALS</div>
                <div style={{ fontSize: 13, color: '#52525b', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {selectedConnector.hasApiKey
                    ? <><Key size={12} style={{ color: '#16a34a' }} /> API key configured</>
                    : selectedConnector._isConfigured || selectedConnector._hasOAuthCreds
                      ? <><Key size={12} style={{ color: '#16a34a' }} /> Credentials configured</>
                      : selectedConnector.authMethod === 'none'
                        ? 'Not required'
                        : <><AlertTriangle size={12} style={{ color: '#d97706' }} /> Not configured — edit to add credentials</>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#a1a1aa', marginBottom: 4 }}>LAST TEST</div>
                <div style={{ fontSize: 13, color: '#52525b', display: 'flex', alignItems: 'center', gap: 4 }}>
                  {selectedConnector.lastTestResult === 'SUCCESS'
                    ? <CheckCircle size={12} style={{ color: '#16a34a' }} />
                    : selectedConnector.lastTestResult === 'FAILED'
                      ? <XCircle size={12} style={{ color: '#ef4444' }} />
                      : null}
                  {selectedConnector.lastTestResult || 'Never tested'}
                </div>
              </div>
            </div>
            {selectedConnector.url && (
              <div>
                <div style={{ fontSize: 11, color: '#a1a1aa', marginBottom: 4 }}>URL</div>
                {/* Detect unresolved template placeholders like "{instanceUrl}" or
                    "https://{tenantId}.foo.com" and render a hint instead of
                    the raw template — the admin still needs to fill these in. */}
                {/\{[^}]+\}/.test(selectedConnector.url) ? (
                  <div style={{ fontSize: 12, color: '#a16207', fontStyle: 'italic' }}>
                    Template URL — fill in admin configuration to resolve:
                    <div style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#78716c', marginTop: 2, wordBreak: 'break-all' }}>
                      {selectedConnector.url}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#52525b', fontFamily: "'IBM Plex Mono', monospace", wordBreak: 'break-all' }}>
                    {selectedConnector.url}
                  </div>
                )}
              </div>
            )}
            {selectedConnector.description && (
              <div>
                <div style={{ fontSize: 11, color: '#a1a1aa', marginBottom: 4 }}>DESCRIPTION</div>
                <div style={{ fontSize: 13, color: '#52525b' }}>{selectedConnector.description}</div>
              </div>
            )}

            {/* ── OAuth User Connection ───────────────────────────── */}
            {/* Only show for authorization_code flow connectors — NOT password grant (e.g. ServiceNow) */}
            {selectedConnector.authMethod === 'oauth2' && (() => {
              const mcRef = findMarketplaceRef(selectedConnector);
              const grantType = mcRef?.authConfig?.grantType;
              // password grant connectors exchange credentials server-side — no user redirect needed
              return grantType !== 'password';
            })() && (
              <div style={{
                borderTop: '1px solid #e4e4e7', paddingTop: 16,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#18181b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Key size={14} /> My Account Connection
                </div>
                {(() => {
                  const conn = myConnections[selectedConnector.id];
                  if (conn && conn.tokenStatus === 'active' && !conn.isExpired) {
                    return (
                      <div style={{
                        padding: '12px 16px', background: '#f0fdf4', borderRadius: 8,
                        border: '1px solid #bbf7d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <CheckCircle size={14} /> Connected
                          </div>
                          {conn.providerEmail && (
                            <div style={{ fontSize: 12, color: '#15803d', marginTop: 2 }}>{conn.providerEmail}</div>
                          )}
                          {conn.grantedScopes && (
                            <div style={{ fontSize: 10, color: '#4ade80', marginTop: 4, wordBreak: 'break-all' }}>
                              Scopes: {conn.grantedScopes}
                            </div>
                          )}
                        </div>
                        <button onClick={() => handleConnectAccount(selectedConnector.id)}
                          disabled={connectingOAuth === selectedConnector.id}
                          style={{
                            padding: '6px 14px', borderRadius: 6, border: '1px solid #bbf7d0',
                            background: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#166534',
                          }}>
                          <RefreshCw size={11} /> Re-connect
                        </button>
                      </div>
                    );
                  } else {
                    return (
                      <div style={{
                        padding: '16px', background: '#f5f5f4', borderRadius: 8,
                        border: '1px solid #bfdbfe', textAlign: 'center',
                      }}>
                        <div style={{ fontSize: 13, color: '#1e40af', marginBottom: 8 }}>
                          Connect your account to enable real API access for skills using this connector.
                        </div>
                        <button onClick={() => handleConnectAccount(selectedConnector.id)}
                          disabled={connectingOAuth === selectedConnector.id}
                          style={{
                            padding: '10px 24px', borderRadius: 8, border: 'none',
                            background: '#2563eb', color: '#fff', cursor: 'pointer',
                            fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8,
                          }}>
                          {connectingOAuth === selectedConnector.id ? (
                            <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Redirecting...</>
                          ) : (
                            <><ExternalLink size={14} /> Connect My Account</>
                          )}
                        </button>
                        <div style={{ fontSize: 11, color: '#60a5fa', marginTop: 8 }}>
                          You'll be redirected to the provider to authorize access.
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
            )}

            {/* ── Password-grant OAuth info (e.g. ServiceNow) ─────── */}
            {selectedConnector.authMethod === 'oauth2' && (() => {
              const mcRef = findMarketplaceRef(selectedConnector);
              return mcRef?.authConfig?.grantType === 'password';
            })() && (
              <div style={{ borderTop: '1px solid #e4e4e7', paddingTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#18181b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Key size={14} /> Authentication
                </div>
                <div style={{
                  padding: '12px 16px', background: '#f0fdf4', borderRadius: 8,
                  border: '1px solid #bbf7d0',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Shield size={14} /> Credentials managed via admin configuration
                  </div>
                  <div style={{ fontSize: 12, color: '#15803d', marginTop: 4 }}>
                    This connector uses OAuth2 password grant. Authentication is handled server-side using the credentials configured by your admin in the connector settings. No separate account connection is needed.
                  </div>
                </div>
              </div>
            )}

            <div style={{ borderTop: '1px solid #e4e4e7', paddingTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#18181b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Link2 size={14} /> Linked Skills ({linkedSkills.length})
              </div>
              {linkedSkills.length === 0 ? (
                <div style={{ fontSize: 12, color: '#a1a1aa' }}>
                  No skills linked. Use the skill editor in the Agents page to link connectors.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {linkedSkills.map((link) => (
                    <div key={link.linkId} style={{
                      padding: 10, background: '#fafaf9', borderRadius: 6,
                      border: '1px solid #f4f4f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: '#18181b' }}>{link.skillName || 'Unknown Skill'}</div>
                        <div style={{ fontSize: 11, color: '#71717a' }}>{link.skillCategory} · {link.purpose}</div>
                      </div>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: '#ede9fe', color: '#7c3aed' }}>
                        {link.purpose}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* ═══ Create / Edit Modal ═════════════════════════════════════════════ */}
      <Modal isOpen={showForm || !!editingConnector}
        title={editingConnector ? (marketplaceRef ? `Configure ${marketplaceRef.name}` : 'Edit Connector') : 'Add Connector'}
        onClose={() => { setShowForm(false); setEditingConnector(null); setMarketplaceRef(null); setConfigForm({}); resetForm(); }}
        actions={
          <>
            <button onClick={() => { setShowForm(false); setEditingConnector(null); setMarketplaceRef(null); setConfigForm({}); resetForm(); }} style={{
              padding: '8px 16px', borderRadius: 6, border: '1px solid #e4e4e7',
              background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>Cancel</button>
            <button onClick={editingConnector ? handleUpdate : handleCreate} style={{
              padding: '8px 16px', borderRadius: 6, border: 'none', background: '#52525b',
              color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>{editingConnector ? 'Save Changes' : 'Add'}</button>
          </>
        }>
        {/* ── Marketplace connector with configSchema ── */}
        {editingConnector && marketplaceRef?.configSchema ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Connector identity (read-only summary) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#fafaf9', borderRadius: 8, border: '1px solid #f4f4f5' }}>
              <span style={{ fontSize: 24 }}>{marketplaceRef.icon}</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#18181b' }}>{marketplaceRef.name}</div>
                <div style={{ fontSize: 11, color: '#71717a' }}>
                  v{marketplaceRef.version} · {marketplaceRef.category} · {marketplaceRef.skills.length} skills
                </div>
              </div>
            </div>

            {/* Endpoint URL — hide for non-REST connectors (SMTP, etc.) */}
            {marketplaceRef.authType !== 'smtp_credentials' && (
              <div>
                <label style={labelStyle}>Endpoint URL</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="url" value={form.url}
                    onChange={(e) => { setForm({ ...form, url: e.target.value }); setUrlTestResult(null); }}
                    style={{ ...inputStyle, flex: 1, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }} />
                  <button onClick={handleTestUrl} disabled={testingUrl || !form.url.trim()} style={{
                    padding: '8px 14px', borderRadius: 6, border: '1px solid #e4e4e7',
                    background: testingUrl ? '#f4f4f5' : '#fff', cursor: testingUrl ? 'not-allowed' : 'pointer',
                    fontSize: 11, fontWeight: 600, color: '#52525b', whiteSpace: 'nowrap',
                    display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                    <Zap size={12} /> {testingUrl ? 'Testing...' : 'Test URL'}
                  </button>
                </div>
              </div>
            )}

            {/* SMTP info banner for Email connectors */}
            {marketplaceRef.authType === 'smtp_credentials' && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, background: '#f5f5f4', border: '1px solid #a8a29e',
                fontSize: 12, color: '#1e40af', lineHeight: 1.5,
              }}>
                <strong>📧 SMTP Connector</strong> — This connector sends email directly via SMTP (not a REST API).
                Configure your email provider, host, port, and app password below. No endpoint URL required.
              </div>
            )}

            {/* ConfigSchema fields */}
            <div style={{ fontSize: 11, fontWeight: 600, color: '#18181b', textTransform: 'uppercase', letterSpacing: '0.05em', borderTop: '1px solid #e4e4e7', paddingTop: 12 }}>
              Connector Configuration
            </div>
            {marketplaceRef.configSchema.map((field) => (
              <div key={field.field}>
                <label style={labelStyle}>
                  {field.label}
                  {field.required && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
                  {field.encrypted && <Key size={10} style={{ marginLeft: 4, color: '#a1a1aa' }} />}
                </label>
                {field.type === 'select' && field.options ? (
                  <select
                    value={configForm[field.field] || ''}
                    onChange={(e) => {
                      const newVal = e.target.value;
                      const updated = { ...configForm, [field.field]: newVal };
                      // Auto-fill related fields from providerPresets when a provider is selected
                      // e.g., selecting "gmail" from smtp_provider fills smtp_host, smtp_port, smtp_encryption
                      if (field.field === 'smtp_provider' && marketplaceRef?.providerPresets?.[newVal]) {
                        const preset = marketplaceRef.providerPresets[newVal];
                        Object.entries(preset).forEach(([k, v]) => {
                          updated[k] = String(v);
                        });
                      }
                      setConfigForm(updated);
                    }}
                    style={{ ...inputStyle, fontSize: 12, background: '#fff', cursor: 'pointer' }}
                  >
                    <option value="">— Select —</option>
                    {field.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={configForm[field.field] || ''}
                    onChange={(e) => setConfigForm({ ...configForm, [field.field]: e.target.value })}
                    rows={3}
                    style={{ ...inputStyle, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, resize: 'vertical', minHeight: 60 }}
                    placeholder={field.placeholder || ''}
                  />
                ) : field.type === 'file' ? (
                  <div style={{ padding: '10px 14px', borderRadius: 6, border: '1px dashed #d4d4d8', background: '#fafaf9', fontSize: 11, color: '#71717a', textAlign: 'center', cursor: 'pointer' }}>
                    Drop {field.accept || ''} file here or click to browse
                  </div>
                ) : (
                  <input
                    type={field.type === 'secret' ? 'password' : field.type || 'text'}
                    value={configForm[field.field] || ''}
                    onChange={(e) => setConfigForm({ ...configForm, [field.field]: e.target.value })}
                    onFocus={(e) => {
                      // Clear sentinel placeholder when user clicks into password field to enter new value
                      if (field.type === 'secret' && configForm[field.field] === '••••••••') {
                        setConfigForm({ ...configForm, [field.field]: '' });
                      }
                    }}
                    style={{ ...inputStyle, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}
                    placeholder={field.placeholder || ''}
                  />
                )}
                {field.type === 'secret' && configForm[field.field] === '••••••••' && (
                  <div style={{ fontSize: 10, color: '#16a34a', marginTop: 3 }}>✓ Password saved — click field to change</div>
                )}
                {field.helpText && (
                  <div style={{ fontSize: 10, color: '#a1a1aa', marginTop: 3, lineHeight: 1.4 }}>{field.helpText}</div>
                )}
              </div>
            ))}

            {/* Scope groups info */}
            {marketplaceRef.scopeGroups && marketplaceRef.scopeGroups.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#18181b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, borderTop: '1px solid #e4e4e7', paddingTop: 12 }}>
                  OAuth Scope Groups
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {marketplaceRef.scopeGroups.map((sg, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px',
                      background: '#fafaf9', borderRadius: 6, border: '1px solid #f4f4f5',
                    }}>
                      <span style={{
                        padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, flexShrink: 0, marginTop: 2,
                        background: sg.required ? '#dcfce7' : '#f4f4f5',
                        color: sg.required ? '#166534' : '#71717a',
                        border: `1px solid ${sg.required ? '#bbf7d0' : '#e4e4e7'}`,
                      }}>
                        {sg.required ? 'required' : 'optional'}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#18181b' }}>{sg.group}</div>
                        <code style={{ fontSize: 10, color: '#71717a', wordBreak: 'break-all' }}>{sg.scopes}</code>
                        {sg.note && <div style={{ fontSize: 10, color: '#a1a1aa', marginTop: 2 }}>{sg.note}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skills credential summary */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#18181b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, borderTop: '1px solid #e4e4e7', paddingTop: 12 }}>
                Skills &amp; Credential Requirements
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {marketplaceRef.skills.map((skill) => (
                  <div key={skill.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    padding: '6px 10px', background: '#fafaf9', borderRadius: 6, border: '1px solid #f4f4f5', fontSize: 11,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MethodBadge method={skill.method} />
                      <span style={{ fontWeight: 500, color: '#18181b' }}>{skill.name}</span>
                    </div>
                    {skill.credentialType && (
                      <span style={{
                        padding: '2px 7px', borderRadius: 4, fontSize: 9, fontWeight: 600,
                        background: skill.credentialType === 'service_account' ? '#fef3c7' : skill.credentialType === 'user_oauth' ? '#dbeafe' : '#f4f4f5',
                        color: skill.credentialType === 'service_account' ? '#92400e' : skill.credentialType === 'user_oauth' ? '#1e40af' : '#52525b',
                      }}>
                        {skill.credentialType === 'user_oauth' ? 'User OAuth' : skill.credentialType === 'service_account' ? 'Service Acct' : skill.credentialType}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
        /* ── Generic connector form (non-marketplace or Add New) ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Endpoint URL</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="url" value={form.url}
                onChange={(e) => { setForm({ ...form, url: e.target.value }); setUrlTestResult(null); }}
                style={{ ...inputStyle, flex: 1, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}
                placeholder="https://api.example.com/v1" />
              <button onClick={handleTestUrl} disabled={testingUrl || !form.url.trim()} style={{
                padding: '8px 14px', borderRadius: 6, border: '1px solid #e4e4e7',
                background: testingUrl ? '#f4f4f5' : '#fff', cursor: testingUrl ? 'not-allowed' : 'pointer',
                fontSize: 11, fontWeight: 600, color: '#52525b', whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
                <Zap size={12} /> {testingUrl ? 'Testing...' : 'Test URL'}
              </button>
            </div>
            {urlTestResult && (
              <div style={{
                marginTop: 8, padding: '8px 12px', borderRadius: 6, fontSize: 12,
                display: 'flex', alignItems: 'center', gap: 8,
                background: urlTestResult.reachable ? '#dcfce7' : '#fef2f2',
                border: `1px solid ${urlTestResult.reachable ? '#bbf7d0' : '#fecaca'}`,
                color: urlTestResult.reachable ? '#166534' : '#991b1b',
              }}>
                {urlTestResult.reachable
                  ? <CheckCircle size={14} style={{ color: '#16a34a', flexShrink: 0 }} />
                  : <XCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />}
                <span style={{ fontWeight: 600 }}>{urlTestResult.reachable ? 'Reachable' : 'Unreachable'}</span>
                {urlTestResult.statusCode && (
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>HTTP {urlTestResult.statusCode}</span>
                )}
                {urlTestResult.latencyMs != null && (
                  <span style={{ fontSize: 11, opacity: 0.7 }}>{urlTestResult.latencyMs}ms</span>
                )}
                {urlTestResult.error && !urlTestResult.reachable && (
                  <span style={{ fontSize: 11 }}>{urlTestResult.error}</span>
                )}
              </div>
            )}
            {urlTestResult?.note && (
              <div style={{
                marginTop: 6, padding: '8px 12px', borderRadius: 6, fontSize: 11, lineHeight: 1.5,
                background: '#fefce8', border: '1px solid #fde68a', color: '#854d0e',
              }}>
                {urlTestResult.note}
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Connector Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={inputStyle} placeholder="e.g. JSONPlaceholder, OpenWeather API" />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
              placeholder="What this connector does..." />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} style={inputStyle}>
                {CONNECTOR_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.value}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Auth Method</label>
              <select value={form.authMethod} onChange={(e) => setForm({ ...form, authMethod: e.target.value })} style={inputStyle}>
                {AUTH_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
          {form.authMethod === 'none' && (
            <div style={{
              padding: '10px 14px', borderRadius: 6, fontSize: 12, lineHeight: 1.5,
              background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
            }}>
              <strong>No Auth</strong> — This connector will call the URL without any credentials.
              Suitable for public REST APIs, open data endpoints, and health-check URLs.
            </div>
          )}
          {form.authMethod === 'api_key' && (
            <>
              <div>
                <label style={labelStyle}>Auth Header Name</label>
                <input value={form.authHeader}
                  onChange={(e) => setForm({ ...form, authHeader: e.target.value })}
                  style={{ ...inputStyle, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 }}
                  placeholder="e.g. Authorization, x-api-key, X-Goog-Api-Key" />
                <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 4 }}>
                  The HTTP header used to send your API key. Defaults to <code>Authorization</code> if left blank.
                </div>
              </div>
              <div>
                <label style={labelStyle}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Key size={12} /> API Key / Token
                  </span>
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={form.apiKey}
                      onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                      style={{ ...inputStyle, fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, paddingRight: 36 }}
                      placeholder="sk-... or your API key"
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                        color: '#71717a', display: 'flex', alignItems: 'center',
                      }}
                      title={showApiKey ? 'Hide API key' : 'Show API key'}
                    >
                      {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#a1a1aa', marginTop: 4 }}>
                  Your API key is stored on the server and used only for connection testing. It is never displayed in logs or agent context.
                </div>
              </div>
              {!form.apiKey && editingConnector && !editingConnector.hasApiKey && (
                <div style={{
                  padding: '10px 14px', borderRadius: 6, fontSize: 12, lineHeight: 1.5,
                  background: '#fef9c3', border: '1px solid #fde68a', color: '#854d0e',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                  <span><strong>No API key configured.</strong> Add your API key above, then Save and Test the connection to activate this connector.</span>
                </div>
              )}
              {!form.apiKey && editingConnector && editingConnector.hasApiKey && (
                <div style={{
                  padding: '10px 14px', borderRadius: 6, fontSize: 12, lineHeight: 1.5,
                  background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <CheckCircle size={14} style={{ flexShrink: 0 }} />
                  <span><strong>API key is configured.</strong> Leave the field blank to keep the existing key, or enter a new key to replace it.</span>
                </div>
              )}
            </>
          )}
          {(form.authMethod === 'oauth2' || form.authMethod === 'basic' || form.authMethod === 'saml' || form.authMethod === 'mtls') && (
            <div style={{
              padding: '10px 14px', borderRadius: 6, fontSize: 12, lineHeight: 1.5,
              background: '#ede9fe', border: '1px solid #ddd6fe', color: '#5b21b6',
            }}>
              <strong>{AUTH_METHODS.find((m) => m.value === form.authMethod)?.label}</strong> — Advanced auth configuration.
              For now, you can store a pre-generated token in the API Key field by selecting "API Key" as the auth method.
            </div>
          )}
        </div>
        )}
      </Modal>
    </V2PageShell>
  );
}
